import { useRef, useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { FormField } from "./FormField";
import { ROLES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMPTY = { name: "", email: "", company: "", role: "", message: "" };
const newToken = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const validate = (v) => {
  const e = {};
  if (!v.name.trim()) e.name = "Please enter your name.";
  if (!v.email.trim()) e.email = "Please enter your work email.";
  else if (!EMAIL_RE.test(v.email.trim())) e.email = "Please enter a valid email address.";
  if (!v.company.trim()) e.company = "Please enter your company.";
  return e;
};

export const DemoForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const busy = useRef(false);
  const token = useRef(newToken());

  const update = (k) => (ev) => {
    const next = { ...values, [k]: ev.target.value };
    setValues(next);
    if (errors[k]) setErrors((p) => ({ ...p, [k]: validate(next)[k] }));
  };
  const blur = (k) => () => { if (values[k]) setErrors((p) => ({ ...p, [k]: validate(values)[k] })); };

  const submit = async (ev) => {
    ev.preventDefault();
    if (busy.current) return;
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    busy.current = true;
    setStatus("submitting");
    try {
      await axios.post(`${API}/demo-requests`, {
        name: values.name.trim(), email: values.email.trim(), company: values.company.trim(),
        role: values.role || null, message: values.message.trim() || null, client_token: token.current,
      });
      setStatus("success");
      setValues(EMPTY);
      token.current = newToken();
    } catch (e) {
      setStatus("error");
    } finally {
      busy.current = false;
    }
  };

  if (status === "success") {
    return (
      <div className="flex min-h-[420px] flex-col justify-center border border-[#bdbdb4] bg-[#fbfbf6] p-8 sm:p-10" data-testid="demo-form-success">
        <CheckCircle2 className="h-8 w-8 text-[#ff6600]" strokeWidth={1.8} />
        <p className="mt-6 font-display text-2xl font-semibold tracking-[-0.02em] text-[#292929] sm:text-3xl" data-testid="demo-form-success-message">
          Thanks — we'll be in touch within 1 business day.
        </p>
        <button type="button" onClick={() => setStatus("idle")} className="mt-8 self-start font-mono text-xs uppercase tracking-[0.16em] text-[#d65300] underline-offset-4 hover:underline" data-testid="demo-form-reset-button">
          Submit another request
        </button>
      </div>
    );
  }

  const field = (k) => ({
    id: `demo-${k}`, name: k, value: values[k], onChange: update(k), onBlur: blur(k),
    "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `demo-${k}-error` : undefined,
    className: `cd-input ${errors[k] ? "cd-input-error" : ""}`, "data-testid": `demo-form-${k}-input`,
  });

  return (
    <form onSubmit={submit} noValidate className="grid gap-6 border border-[#bdbdb4] bg-[#fbfbf6] p-6 sm:p-10" data-testid="demo-form">
      <div className="grid gap-6 sm:grid-cols-2">
        <FormField id="demo-name" label="Name" error={errors.name}><input type="text" autoComplete="name" {...field("name")} /></FormField>
        <FormField id="demo-email" label="Work email" error={errors.email}><input type="email" autoComplete="email" inputMode="email" {...field("email")} /></FormField>
        <FormField id="demo-company" label="Company" error={errors.company}><input type="text" autoComplete="organization" {...field("company")} /></FormField>
        <FormField id="demo-role" label="Role" optional>
          <select {...field("role")}>
            <option value="">Select your role</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </FormField>
      </div>
      <FormField id="demo-message" label="Message" optional>
        <textarea rows={4} placeholder="Project, openings, timeline…" {...field("message")} />
      </FormField>
      {status === "error" && (
        <p className="border-l-2 border-[#c2410c] bg-[#fff0e5] px-4 py-3 text-sm text-[#9a3412]" role="alert" data-testid="demo-form-error-message">
          Something went wrong. Please try again.
        </p>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <span />
        <button type="submit" disabled={status === "submitting"} className="cd-btn-primary h-12 px-7 text-[15px] disabled:cursor-not-allowed disabled:opacity-60" data-testid="demo-form-submit-button">
          {status === "submitting" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Book a demo <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </form>
  );
};
